/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Server-side outbound URL guard (OWASP SSRF baseline).
 */

export class UnsafeOutboundUrlError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnsafeOutboundUrlError";
  }
}

const BLOCKED_HOSTNAMES = new Set(
  [
    "localhost",
    "0.0.0.0",
    "127.0.0.1",
    "169.254.169.254",
    "metadata.google.internal",
    "metadata",
  ].map((h) => h.toLowerCase()),
);

/**
 * Exact hostname allowlist for outbound fetches (no substring matching).
 * Add entries only when an API route legitimately fetches that host.
 */
const ALLOWED_OUTBOUND_HOSTNAMES = new Set(
  [
    "oauth.platform.intuit.com",
    "quickbooks.api.intuit.com",
    "sandbox-quickbooks.api.intuit.com",
    "generativelanguage.googleapis.com",
    "api.openai.com",
    "api.anthropic.com",
    "api.tavily.com",
    "maps.googleapis.com",
    "gmail.googleapis.com",
    "www.googleapis.com",
    "oauth2.googleapis.com",
    "accounts.google.com",
    "graph.microsoft.com",
    "login.microsoftonline.com",
    "api.mapbox.com",
    "replicate.delivery",
    "gateway-external.1build.com",
    "api.xero.com",
    // DocuSign eSignature (JWT + REST); fixed vendor hosts only
    "account.docusign.com",
    "account-d.docusign.com",
    "account-s.docusign.com",
    "www.docusign.net",
    "demo.docusign.net",
    "stage.docusign.net",
  ].map((h) => h.toLowerCase()),
);

export type AssertSafeHttpUrlOptions = {
  /**
   * When set, a URL whose origin equals this origin is treated as a same-app
   * internal fetch (e.g. server-side PDF export) and is exempt from the
   * outbound hostname allowlist. Still subject to scheme + basic safety checks.
   */
  internalAppOrigin?: string;
  /**
   * Exact hostnames (lowercase) permitted for this call only — e.g. the API
   * host parsed from `NEXT_PUBLIC_SUPABASE_URL` for edge-function fetches.
   */
  extraAllowedHostnames?: readonly string[];
  /**
   * Skip the outbound hostname allowlist while still enforcing http(s), blocked
   * hostnames, and private/reserved literal IPs. Use sparingly for URLs whose
   * host is user-controlled but must remain public-internet (e.g. org logo URL,
   * email attachment CDN).
   */
  relaxOutboundHostnameAllowlist?: boolean;
};

function isPrivateOrLoopbackIpv4(
  octets: Readonly<[number, number, number, number]>,
): boolean {
  const [a, b] = octets;
  if (a === 127) return true;
  if (a === 0) return true;
  if (a === 10) return true;
  if (a === 169 && b === 254) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  return false;
}

function parseIpv4Host(
  hostname: string,
): readonly [number, number, number, number] | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(hostname);
  if (!m) return null;
  const parts = [
    Number(m[1]),
    Number(m[2]),
    Number(m[3]),
    Number(m[4]),
  ] as const;
  if (parts.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return null;
  return parts;
}

function assertIpv6HostSafe(hostname: string): void {
  const inner = hostname.slice(1, -1).toLowerCase();
  if (inner === "::1" || inner === "0:0:0:0:0:0:0:1") {
    throw new UnsafeOutboundUrlError("IPv6 loopback is not allowed");
  }
  if (inner.endsWith("127.0.0.1") || inner.includes("ffff:127.0.0.1")) {
    throw new UnsafeOutboundUrlError("IPv4-mapped loopback is not allowed");
  }
}

function originsMatchSafe(a: URL, b: URL): boolean {
  return (
    a.protocol === b.protocol &&
    a.hostname.toLowerCase() === b.hostname.toLowerCase() &&
    a.port === b.port
  );
}

function isOutboundHostnameAllowed(
  hostLower: string,
  options?: AssertSafeHttpUrlOptions,
): boolean {
  if (ALLOWED_OUTBOUND_HOSTNAMES.has(hostLower)) {
    return true;
  }
  const extras = options?.extraAllowedHostnames;
  if (extras?.some((h) => h.toLowerCase() === hostLower)) {
    return true;
  }
  return false;
}

/**
 * Validates a URL for server-side HTTP(S) fetch. Throws if scheme/host is unsafe.
 *
 * Unknown hostnames are rejected (exact allowlist, no substring rules).
 * Option `internalAppOrigin` exempts same-origin absolute URLs for in-app fetches.
 */
export function assertSafeHttpUrlForServerFetch(
  raw: string,
  options?: AssertSafeHttpUrlOptions,
): URL {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new UnsafeOutboundUrlError("Invalid URL");
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new UnsafeOutboundUrlError("Only http(s) URLs are allowed");
  }

  const hostname = url.hostname;
  if (!hostname) {
    throw new UnsafeOutboundUrlError("Missing hostname");
  }

  if (options?.internalAppOrigin) {
    let appOriginUrl: URL;
    try {
      appOriginUrl = new URL(options.internalAppOrigin);
    } catch {
      throw new UnsafeOutboundUrlError("Invalid internalAppOrigin");
    }
    if (originsMatchSafe(url, appOriginUrl)) {
      return url;
    }
  }

  const hostLower = hostname.toLowerCase();
  if (BLOCKED_HOSTNAMES.has(hostLower)) {
    throw new UnsafeOutboundUrlError("Blocked hostname");
  }

  const ipv4 = parseIpv4Host(hostLower);
  if (ipv4) {
    if (isPrivateOrLoopbackIpv4(ipv4)) {
      throw new UnsafeOutboundUrlError("Blocked IPv4 range");
    }
    if (options?.relaxOutboundHostnameAllowlist) {
      return url;
    }
    if (!isOutboundHostnameAllowed(hostLower, options)) {
      throw new UnsafeOutboundUrlError(
        "Hostname is not on the outbound allowlist",
      );
    }
    return url;
  }

  if (hostLower.startsWith("[") && hostLower.endsWith("]")) {
    assertIpv6HostSafe(hostLower);
    const ipv6Inner = hostLower.slice(1, -1).toLowerCase();
    if (options?.relaxOutboundHostnameAllowlist) {
      return url;
    }
    if (!isOutboundHostnameAllowed(ipv6Inner, options)) {
      throw new UnsafeOutboundUrlError(
        "Hostname is not on the outbound allowlist",
      );
    }
    return url;
  }

  if (options?.relaxOutboundHostnameAllowlist) {
    return url;
  }

  if (!isOutboundHostnameAllowed(hostLower, options)) {
    throw new UnsafeOutboundUrlError(
      "Hostname is not on the outbound allowlist",
    );
  }

  return url;
}
